// Design tokens: four skins × light/dark, expressed as CSS variables that the
// Tailwind palette is remapped onto (see tailwind.config.js). Because every
// existing utility class (bg-gray-800, text-cyan-300 …) resolves to one of these
// variables, switching skin or mode re-themes the entire app without touching
// component markup. Dark mode keeps Tailwind's familiar values; light mode
// shifts "text-ish" accent shades (100–400) darker so contrast stays WCAG AA.

// Tailwind v3.4 default palettes (only the families we alias onto).
const TW = {
  cyan:    { 100:"#cffafe",200:"#a5f3fc",300:"#67e8f9",400:"#22d3ee",500:"#06b6d4",600:"#0891b2",700:"#0e7490",800:"#155e75",900:"#164e63" },
  teal:    { 100:"#ccfbf1",200:"#99f6e4",300:"#5eead4",400:"#2dd4bf",500:"#14b8a6",600:"#0d9488",700:"#0f766e",800:"#115e59",900:"#134e4a" },
  sky:     { 100:"#e0f2fe",200:"#bae6fd",300:"#7dd3fc",400:"#38bdf8",500:"#0ea5e9",600:"#0284c7",700:"#0369a1",800:"#075985",900:"#0c4a6e" },
  blue:    { 100:"#dbeafe",200:"#bfdbfe",300:"#93c5fd",400:"#60a5fa",500:"#3b82f6",600:"#2563eb",700:"#1d4ed8",800:"#1e40af",900:"#1e3a8a" },
  indigo:  { 100:"#e0e7ff",200:"#c7d2fe",300:"#a5b4fc",400:"#818cf8",500:"#6366f1",600:"#4f46e5",700:"#4338ca",800:"#3730a3",900:"#312e81" },
  violet:  { 100:"#ede9fe",200:"#ddd6fe",300:"#c4b5fd",400:"#a78bfa",500:"#8b5cf6",600:"#7c3aed",700:"#6d28d9",800:"#5b21b6",900:"#4c1d95" },
  purple:  { 100:"#f3e8ff",200:"#e9d5ff",300:"#d8b4fe",400:"#c084fc",500:"#a855f7",600:"#9333ea",700:"#7e22ce",800:"#6b21a8",900:"#581c87" },
  fuchsia: { 100:"#fae8ff",200:"#f5d0fe",300:"#f0abfc",400:"#e879f9",500:"#d946ef",600:"#c026d3",700:"#a21caf",800:"#86198f",900:"#701a75" },
  pink:    { 100:"#fce7f3",200:"#fbcfe8",300:"#f9a8d4",400:"#f472b6",500:"#ec4899",600:"#db2777",700:"#be185d",800:"#9d174d",900:"#831843" },
  rose:    { 100:"#ffe4e6",200:"#fecdd3",300:"#fda4af",400:"#fb7185",500:"#f43f5e",600:"#e11d48",700:"#be123c",800:"#9f1239",900:"#881337" },
  red:     { 100:"#fee2e2",200:"#fecaca",300:"#fca5a5",400:"#f87171",500:"#ef4444",600:"#dc2626",700:"#b91c1c",800:"#991b1b",900:"#7f1d1d" },
  orange:  { 100:"#ffedd5",200:"#fed7aa",300:"#fdba74",400:"#fb923c",500:"#f97316",600:"#ea580c",700:"#c2410c",800:"#9a3412",900:"#7c2d12" },
  amber:   { 100:"#fef3c7",200:"#fde68a",300:"#fcd34d",400:"#fbbf24",500:"#f59e0b",600:"#d97706",700:"#b45309",800:"#92400e",900:"#78350f" },
  yellow:  { 100:"#fef9c3",200:"#fef08a",300:"#fde047",400:"#facc15",500:"#eab308",600:"#ca8a04",700:"#a16207",800:"#854d0e",900:"#713f12" },
  green:   { 100:"#dcfce7",200:"#bbf7d0",300:"#86efac",400:"#4ade80",500:"#22c55e",600:"#16a34a",700:"#15803d",800:"#166534",900:"#14532d" },
  emerald: { 100:"#d1fae5",200:"#a7f3d0",300:"#6ee7b7",400:"#34d399",500:"#10b981",600:"#059669",700:"#047857",800:"#065f46",900:"#064e3b" },
};

// The "gray" family = each skin's neutral scale. Semantics follow Tailwind's
// dark-UI usage: 900 page bg, 800 cards, 700 inputs, 600 borders, 500
// placeholder, 400 muted text, 300 text, 200 bright text, 100 strongest.
const NEUTRAL = {
  aurora: {
    dark:  { 100:"#f1f5f9",200:"#e2e8f0",300:"#cbd5e1",400:"#94a3b8",500:"#64748b",600:"#2a3752",700:"#1b2740",800:"#111a2e",900:"#0b1220" },
    light: { 100:"#0f172a",200:"#243041",300:"#3b4657",400:"#5b6676",500:"#7b8798",600:"#d7dfe9",700:"#eef2f7",800:"#ffffff",900:"#f4f7fb" },
  },
  paper: {
    dark:  { 100:"#f7f1e8",200:"#ebe1d3",300:"#d6c8b6",400:"#a89684",500:"#857463",600:"#3d342b",700:"#2c251e",800:"#211c16",900:"#17130f" },
    light: { 100:"#1c1712",200:"#2f2822",300:"#4a3f33",400:"#6b5d4c",500:"#8a7b68",600:"#ddd2c0",700:"#f1e9db",800:"#fffdf8",900:"#f6f1e7" },
  },
  minimal: {
    dark:  { 100:"#f4f4f5",200:"#e4e4e7",300:"#d4d4d8",400:"#a1a1aa",500:"#71717a",600:"#27272a",700:"#18181b",800:"#101012",900:"#09090b" },
    light: { 100:"#18181b",200:"#27272a",300:"#3f3f46",400:"#52525b",500:"#71717a",600:"#e4e4e7",700:"#f4f4f5",800:"#ffffff",900:"#fafafa" },
  },
  bento: {
    dark:  { 100:"#f5f5fa",200:"#e6e6ee",300:"#cfcfdc",400:"#9c9cb0",500:"#6b6b80",600:"#2c2c3a",700:"#1d1d28",800:"#14141c",900:"#0a0a0f" },
    light: { 100:"#10141f",200:"#23293a",300:"#3a4152",400:"#565d6e",500:"#6f7688",600:"#d5d9e3",700:"#eceef4",800:"#ffffff",900:"#f2f3f7" },
  },
};

// Monochrome accent scale used by Neo-minimal for "decorative" families so
// only one real accent (sky) remains.
const MONO = {
  dark:  { 100:"#fafafa",200:"#f4f4f5",300:"#e4e4e7",400:"#d4d4d8",500:"#a1a1aa",600:"#71717a",700:"#52525b",800:"#3f3f46",900:"#27272a" },
  light: { 100:"#18181b",200:"#27272a",300:"#3f3f46",400:"#3f3f46",500:"#71717a",600:"#52525b",700:"#3f3f46",800:"#27272a",900:"#18181b" },
};

// Which real palette each app colour family resolves to, per skin.
const ALIAS = {
  aurora:  { cyan:"cyan", yellow:"yellow", amber:"amber", purple:"purple",  blue:"blue", indigo:"indigo", green:"green",   red:"red",  orange:"orange", pink:"pink" },
  paper:   { cyan:"teal", yellow:"amber",  amber:"amber", purple:"violet",  blue:"sky",  indigo:"indigo", green:"emerald", red:"rose", orange:"orange", pink:"rose" },
  minimal: { cyan:"sky",  yellow:"mono",   amber:"mono",  purple:"mono",    blue:"sky",  indigo:"mono",   green:"emerald", red:"red",  orange:"orange", pink:"mono" },
  bento:   { cyan:"sky",  yellow:"amber",  amber:"amber", purple:"fuchsia", blue:"blue", indigo:"indigo", green:"emerald", red:"rose", orange:"orange", pink:"pink" },
};

// In light mode, shades used as text on dark (100–400) must become darker.
const LIGHT_SHIFT = { 100:800, 200:700, 300:700, 400:600, 500:500, 600:600, 700:700, 800:800, 900:900 };

const FAMILIES = ["gray","cyan","yellow","amber","green","red","orange","purple","blue","indigo","pink"];
const SHADES = [100,200,300,400,500,600,700,800,900];

// Non-palette tokens (backgrounds, glass, radii, brand, ambient).
const SEMANTIC = {
  aurora: {
    dark: {
      base:"#0b1220",
      bg:"radial-gradient(1100px 620px at 12% -8%, rgba(34,211,238,0.14), transparent 60%), radial-gradient(900px 560px at 88% 4%, rgba(250,204,21,0.10), transparent 60%), radial-gradient(800px 500px at 50% 110%, rgba(139,92,246,0.10), transparent 60%), linear-gradient(180deg, #0b1220 0%, #0a0f1c 100%)",
      glassBg:"rgba(17,26,46,0.62)", glassBorder:"rgba(255,255,255,0.08)", glassBlur:"16px",
      rXl:"1.25rem", rLg:"1rem", rMd:"0.75rem",
      brandA:"#facc15", brandB:"#22d3ee", auroraA:"#22d3ee", auroraB:"#facc15", auroraC:"#8b5cf6", auroraI:"0.45",
      shadow:"0 10px 30px -12px rgba(0,0,0,0.6)",
    },
    light: {
      base:"#f4f7fb",
      bg:"radial-gradient(1100px 620px at 12% -8%, rgba(8,145,178,0.12), transparent 60%), radial-gradient(900px 560px at 88% 4%, rgba(217,119,6,0.10), transparent 60%), linear-gradient(180deg, #f6f8fc 0%, #eef2f7 100%)",
      glassBg:"rgba(255,255,255,0.66)", glassBorder:"rgba(15,23,42,0.08)", glassBlur:"16px",
      rXl:"1.25rem", rLg:"1rem", rMd:"0.75rem",
      brandA:"#b45309", brandB:"#0e7490", auroraA:"#0891b2", auroraB:"#d97706", auroraC:"#7c3aed", auroraI:"0.28",
      shadow:"0 10px 30px -14px rgba(15,23,42,0.18)",
    },
  },
  paper: {
    dark: {
      base:"#17130f",
      bg:"radial-gradient(1000px 600px at 15% -10%, rgba(20,184,166,0.10), transparent 60%), radial-gradient(900px 500px at 85% 0%, rgba(245,158,11,0.09), transparent 60%), linear-gradient(180deg, #17130f, #130f0c)",
      glassBg:"rgba(33,28,22,0.66)", glassBorder:"rgba(255,255,255,0.07)", glassBlur:"12px",
      rXl:"1rem", rLg:"0.75rem", rMd:"0.5rem",
      brandA:"#f59e0b", brandB:"#2dd4bf", auroraA:"#14b8a6", auroraB:"#f59e0b", auroraC:"#fb7185", auroraI:"0.35",
      shadow:"0 10px 30px -12px rgba(0,0,0,0.6)",
    },
    light: {
      base:"#f6f1e7",
      bg:"radial-gradient(1000px 600px at 15% -10%, rgba(13,148,136,0.10), transparent 60%), radial-gradient(900px 500px at 85% 0%, rgba(180,83,9,0.10), transparent 60%), linear-gradient(180deg, #f8f3ea, #f1e9db)",
      glassBg:"rgba(255,253,248,0.72)", glassBorder:"rgba(28,23,18,0.08)", glassBlur:"12px",
      rXl:"1rem", rLg:"0.75rem", rMd:"0.5rem",
      brandA:"#92400e", brandB:"#0f766e", auroraA:"#0d9488", auroraB:"#b45309", auroraC:"#be123c", auroraI:"0.22",
      shadow:"0 10px 30px -14px rgba(28,23,18,0.16)",
    },
  },
  minimal: {
    dark: {
      base:"#09090b",
      bg:"radial-gradient(900px 500px at 50% -20%, rgba(56,189,248,0.08), transparent 60%), linear-gradient(180deg, #09090b, #0c0c0f)",
      glassBg:"rgba(16,16,18,0.70)", glassBorder:"rgba(255,255,255,0.06)", glassBlur:"8px",
      rXl:"0.75rem", rLg:"0.5rem", rMd:"0.375rem",
      brandA:"#e4e4e7", brandB:"#38bdf8", auroraA:"#38bdf8", auroraB:"#52525b", auroraC:"#38bdf8", auroraI:"0.25",
      shadow:"0 8px 24px -12px rgba(0,0,0,0.7)",
    },
    light: {
      base:"#fafafa",
      bg:"radial-gradient(900px 500px at 50% -20%, rgba(3,105,161,0.08), transparent 60%), linear-gradient(180deg, #fbfbfc, #f4f4f5)",
      glassBg:"rgba(255,255,255,0.75)", glassBorder:"rgba(24,24,27,0.08)", glassBlur:"8px",
      rXl:"0.75rem", rLg:"0.5rem", rMd:"0.375rem",
      brandA:"#18181b", brandB:"#0369a1", auroraA:"#0369a1", auroraB:"#a1a1aa", auroraC:"#0369a1", auroraI:"0.15",
      shadow:"0 8px 24px -14px rgba(24,24,27,0.16)",
    },
  },
  bento: {
    dark: {
      base:"#0a0a0f",
      bg:"radial-gradient(1000px 600px at 8% -5%, rgba(232,121,249,0.14), transparent 60%), radial-gradient(900px 560px at 92% 8%, rgba(56,189,248,0.14), transparent 60%), radial-gradient(700px 500px at 50% 110%, rgba(251,191,36,0.10), transparent 60%), linear-gradient(180deg, #0a0a0f, #0d0d14)",
      glassBg:"rgba(20,20,28,0.62)", glassBorder:"rgba(255,255,255,0.09)", glassBlur:"18px",
      rXl:"1.25rem", rLg:"1rem", rMd:"0.75rem",
      brandA:"#e879f9", brandB:"#38bdf8", auroraA:"#e879f9", auroraB:"#38bdf8", auroraC:"#fbbf24", auroraI:"0.5",
      shadow:"0 10px 30px -12px rgba(0,0,0,0.65)",
    },
    light: {
      base:"#f2f3f7",
      bg:"radial-gradient(1000px 600px at 8% -5%, rgba(162,28,175,0.10), transparent 60%), radial-gradient(900px 560px at 92% 8%, rgba(3,105,161,0.10), transparent 60%), radial-gradient(700px 500px at 50% 110%, rgba(180,83,9,0.08), transparent 60%), linear-gradient(180deg, #f5f6fa, #eceef4)",
      glassBg:"rgba(255,255,255,0.68)", glassBorder:"rgba(16,20,31,0.08)", glassBlur:"18px",
      rXl:"1.25rem", rLg:"1rem", rMd:"0.75rem",
      brandA:"#a21caf", brandB:"#0369a1", auroraA:"#a21caf", auroraB:"#0369a1", auroraC:"#b45309", auroraI:"0.28",
      shadow:"0 10px 30px -14px rgba(16,20,31,0.18)",
    },
  },
};

export const DEFAULT_SKIN = "aurora";
export const SKINS = [
  { id: "aurora",  name: "Frosted Aurora", tagline: "Deep navy, soft aurora glow, glass cards", preview: { bg: "#0b1220", a: "#22d3ee", b: "#facc15" } },
  { id: "paper",   name: "Warm Paper",     tagline: "Cream & ink, editorial calm",              preview: { bg: "#f6f1e7", a: "#0d9488", b: "#d97706" } },
  { id: "minimal", name: "Neo-minimal",    tagline: "Near-black, hairlines, one accent",        preview: { bg: "#09090b", a: "#38bdf8", b: "#d4d4d8" } },
  { id: "bento",   name: "Bento",          tagline: "Tiles with bold colour blocks",            preview: { bg: "#14141c", a: "#e879f9", b: "#38bdf8" } },
];
export const SKIN_IDS = SKINS.map((s) => s.id);

export function hexToRgbTriplet(hex) {
  const h = String(hex).replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/**
 * Build the full CSS-variable set for a skin + resolved mode ("dark"|"light").
 * Returns { "--c-gray-800": "17 26 46", "--app-bg": "...", ... }.
 */
export function buildVars(skinId, mode) {
  const skin = SKIN_IDS.includes(skinId) ? skinId : DEFAULT_SKIN;
  const m = mode === "light" ? "light" : "dark";
  const vars = {};

  for (const fam of FAMILIES) {
    for (const s of SHADES) {
      let hex;
      if (fam === "gray") {
        hex = NEUTRAL[skin][m][s];
      } else {
        const src = ALIAS[skin][fam];
        if (src === "mono") hex = MONO[m][s];
        else hex = TW[src][m === "light" ? LIGHT_SHIFT[s] : s];
      }
      vars[`--c-${fam}-${s}`] = hexToRgbTriplet(hex);
    }
  }
  // `white` is "strongest foreground": pure white on dark, near-black on light.
  vars["--c-white"] =
    m === "dark" ? "255 255 255" : hexToRgbTriplet(NEUTRAL[skin].light[100]);

  const S = SEMANTIC[skin][m];
  vars["--app-bg-base"] = S.base;
  vars["--app-bg"] = S.bg;
  vars["--glass-bg"] = S.glassBg;
  vars["--glass-border"] = S.glassBorder;
  vars["--glass-blur"] = S.glassBlur;
  vars["--radius-xl"] = S.rXl;
  vars["--radius-lg"] = S.rLg;
  vars["--radius-md"] = S.rMd;
  vars["--brand-a"] = S.brandA;
  vars["--brand-b"] = S.brandB;
  vars["--aurora-a"] = S.auroraA;
  vars["--aurora-b"] = S.auroraB;
  vars["--aurora-c"] = S.auroraC;
  vars["--aurora-intensity"] = S.auroraI;
  vars["--shadow-card"] = S.shadow;
  vars["--theme-color"] = S.base;
  return vars;
}

export function varsToCssText(vars) {
  return `:root{${Object.entries(vars)
    .map(([k, v]) => `${k}:${v}`)
    .join(";")}}`;
}
